/**
 * Sense (Foundry: rule-element/sense.ts): a special sense with an acuity and a range, offered to
 * Perception as a synthetic. A better acuity or a longer range replaces an existing sense of the
 * same kind (`force` replaces regardless); see ../senses.ts.
 */
import { cleanRuleValue, resolveValue } from "../resolve";
import type { EngineState } from "../state";
import { SENSES_WITH_MANDATORY_ACUITIES, SENSES_WITH_UNLIMITED_RANGE, isSenseAcuity, isSenseType, type SenseAcuity, type SenseType } from "../values";
import type { RuleHandler, RuleInstance } from "./base";

export interface SenseRuleData {
  selector: SenseType;
  force: boolean;
  acuity: SenseAcuity | null;
  range: unknown;
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  if (!isSenseType(s.selector)) return rule.fail(`selector: "${String(s.selector)}" is not a sense`);
  if (s.force !== undefined && typeof s.force !== "boolean") return rule.fail("force: must be a boolean");
  if (s.acuity !== undefined && !isSenseAcuity(s.acuity)) return rule.fail(`acuity: "${String(s.acuity)}" is not an acuity`);
  const data: SenseRuleData = { selector: s.selector, force: s.force === true, acuity: isSenseAcuity(s.acuity) ? s.acuity : null, range: cleanRuleValue(s.range) };
  rule.data = data as unknown as Record<string, unknown>;
}

function beforePrepareData(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as SenseRuleData;
  const range = (SENSES_WITH_UNLIMITED_RANGE as readonly string[]).includes(data.selector)
    ? Infinity
    : Math.max(0, Math.trunc(Math.floor(Number(resolveValue(rule, state, data.range, Infinity)) || 0)));
  if (rule.ignored) return;
  if (range <= 0) {
    if (range < 0) rule.fail("range: must resolve to a positive number");
    return;
  }
  state.synthetics.senses.push({
    sense: { type: data.selector, acuity: SENSES_WITH_MANDATORY_ACUITIES[data.selector] ?? data.acuity ?? "precise", range, source: rule.item.name },
    predicate: structuredClone(rule.predicate),
    force: data.force,
  });
}

export const SenseHandler: RuleHandler = { key: "Sense", parse, beforePrepareData };
